'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, createContext, useContext } from 'react';
import { QueryApi } from '@/features/query/api';
import { formatAddress } from '@/shared/lib/formatAddress';
import { Button } from '@/shared/ui/button';
import { Icon as IconCmp } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';

type VenueAddressActionContextValue = {
    address: string | null;
};

const VenueAddressActionContext = createContext<VenueAddressActionContextValue>({ address: null });

type VenueAddressActionWrapperProps = ComponentProps<typeof Layout>;

export const VenueAddressActionWrapper = ({ children, media, ...props }: VenueAddressActionWrapperProps) => {
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const address = venueQuery.data?.address ? formatAddress(venueQuery.data?.address) : null;

    if (!address) {
        return null;
    }

    return (
        <VenueAddressActionContext.Provider value={{ address }}>
            <Layout media={media} {...props}>
                {children}
            </Layout>
        </VenueAddressActionContext.Provider>
    );
};

type VenueAddressActionIconProps = ComponentProps<typeof Layout>;

export const VenueAddressActionIcon = ({ media, ...props }: VenueAddressActionIconProps) => {
    return (
        <Layout
            media={{
                ...media,
                initial: {
                    width: 18,
                    height: 18,
                    ...media?.initial,
                },
            }}
            {...props}
        >
            <IconCmp name="map-pin" data-testid="contact-info-action-icon" width={18} height={18} />
        </Layout>
    );
};

type VenueAddressActionTextProps = ComponentProps<typeof Layout>;

export const VenueAddressActionText = ({ media, ...props }: VenueAddressActionTextProps) => {
    const { address } = useContext(VenueAddressActionContext);
    return (
        <Layout element="span" media={media} data-testid="contact-info-box-address" {...props}>
            {address}
        </Layout>
    );
};

type VenueAddressActionButtonProps = ComponentProps<typeof Layout>;

export const VenueAddressActionButton = ({ children, media, ...props }: VenueAddressActionButtonProps) => {
    const t = useTranslations();
    const { address } = useContext(VenueAddressActionContext);
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address ?? '')}`;

    return (
        <Layout media={media} {...props}>
            <Button size="small" asChild data-testid="contact-info-action-button">
                {children ?? (
                    <a
                        href={mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-info-box-navigate-button"
                    >
                        {t('WORDS.NAVIGATE')}
                    </a>
                )}
            </Button>
        </Layout>
    );
};

// Compound component structure
export const VenueAddressActionCompound: typeof VenueAddressActionWrapper & {
    Icon: typeof VenueAddressActionIcon;
    Text: typeof VenueAddressActionText;
    Button: typeof VenueAddressActionButton;
} = Object.assign(VenueAddressActionWrapper, {
    Icon: VenueAddressActionIcon,
    Text: VenueAddressActionText,
    Button: VenueAddressActionButton,
});
